import { getLength } from 'ol/sphere';
import { Fill, Stroke, RegularShape, Style, Text } from 'ol/style';
import {
  RULER_STYLE,
  MULTI_POINT_STYLE,
  LABEL_POINT_STYLE,
} from '../../../../consts/mapConfig';

export const getRotation = (startCoord, endCoord) => {
  const rotation = Math.atan2(
    endCoord[1] - startCoord[1],
    endCoord[0] - startCoord[0]
  );
  return rotation;
};

export const getLayerFeatureById = (layer, featureId) => {
  if (!layer || !featureId) return;
  return layer.getSource() ? layer.getSource().getFeatureById(featureId) : null;
};

export const clearLabelPointsOnLine = pointsLayer => {
  for (const feature of pointsLayer.current.getSource().getFeatures()) {
    if (feature.getProperties().name === 'hoverLabelPoint')
      pointsLayer.current.getSource().removeFeature(feature);
  }
};

// The function counts ratios used to count points of splitting on LineString.
// It returns an object which contains two values: floatRatioOfSplittingLine and finalRatioOfSplittingLine
// The first one is used to count split segments length
// The second one is used in loop to get sequence of following split points coordinates
export const getRatios = (tmpLineFeature, intersectsLength) => {
  const length = getLength(tmpLineFeature.getGeometry());
  const floatRatioOfSplittingLine = length / intersectsLength;
  const intRoundedRatioOfSplittingLine = floatRatioOfSplittingLine.toFixed(0);
  const finalRatioOfSplittingLine =
    (intersectsLength * intRoundedRatioOfSplittingLine) /
    length /
    intRoundedRatioOfSplittingLine;

  return {
    floatRatioOfSplittingLine,
    finalRatioOfSplittingLine,
  };
};

export const getLineStyle = themeType =>
  new Style({
    stroke: new Stroke(RULER_STYLE(themeType).stroke),
  });

export const getMultiPointStyle = (rotation, themeType) =>
  new Style({
    image: new RegularShape({
      radius: 4,
      points: 2,
      rotation: -rotation,
      displacement: [0, 5],
      stroke: new Stroke(MULTI_POINT_STYLE(themeType).stroke),
    }),
  });

const offsets = (rotation, xOffset, yOffset) => {
  let offsetX = -Math.sin(rotation) * 33;
  let offsetY = -Math.cos(rotation) * 16;
  if (xOffset || yOffset) {
    if (Math.abs(offsetX) < Math.abs(offsetY)) {
      offsetX += xOffset;
    } else {
      offsetY += yOffset;
    }
  }
  return { offsetY, offsetX };
};

export const getLabelPointStyle = (
  themeType,
  value,
  rotation,
  xOffset = 0,
  yOffset = 0
) =>
  new Style({
    text: new Text({
      text: `${value}`,
      font: '10px sans-serif',
      textAlign: 'center',
      textBaseline: 'center',
      ...offsets(rotation, xOffset, yOffset),
      scale: 1.2,
      fill: new Fill(LABEL_POINT_STYLE(themeType).fill),
      stroke: new Stroke(LABEL_POINT_STYLE(themeType).stroke),
    }),
  });

export const DEFAULT_RULER_LABEL_STEP = 2;

export const removeLayerFeature = (layerId, featureId) => {
  if (layerId?.current?.getSource() && featureId) {
    const featureToRemove = getLayerFeatureById(layerId.current, featureId);
    if (featureToRemove) {
      layerId.current.getSource().removeFeature(featureToRemove);
    }
  }
};

export const RULER_FEATURES_IDS = {
  HOVER_RULER_MULTI_POINTS: 'hoverRulerMultiPoints',
  LAST_POINT_LABEL: 'lastPointLabel',
  LAST_POINT_MARKER: 'lastPointMarker',
  POINTER_LINE: 'pointerLine',
  RULER_LINE: 'rulerLine',
  RULER_MULTI_POINTS: 'rulerMultiPoints',
};

export const RULER_FEATURES_NAMES = {
  HOVER_LABEL_POINT: 'hoverLabelPoint',
  LABEL_POINT: 'labelPoint',
  RULER_MULTI_POINT: 'rulerMultiPoint',
};

export const filterOutRulerFeatures = featureList =>
  featureList
    .filter(
      feature => !Object.values(RULER_FEATURES_IDS).includes(feature?.id_)
    )
    .filter(
      feature =>
        !Object.values(RULER_FEATURES_NAMES).includes(
          feature.getProperties()?.name
        )
    );
